import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} pasteTab
 * @property {HTMLButtonElement} decksTab
 * @property {HTMLButtonElement} themeTab
 * @property {HTMLElement} pastePanel
 * @property {HTMLElement} decksPanel
 * @property {HTMLElement} themePanel
 * @property {HTMLTextAreaElement} deckList
 * @property {HTMLInputElement} fileInput
 * @property {HTMLButtonElement} clearButton
 * @property {HTMLButtonElement} checkButton
 * @property {HTMLElement} result
 * @property {HTMLElement} resultSummary
 * @property {HTMLElement} status
 * @property {HTMLUListElement} cardList
 * @property {HTMLElement} total
 * @property {HTMLButtonElement} cartButton
 * @extends {Component<Refs>}
 */
class DicesDeckBuilder extends Component {
  requiredRefs = ['pasteTab', 'decksTab', 'themeTab', 'pastePanel', 'decksPanel', 'themePanel', 'deckList', 'fileInput', 'clearButton', 'checkButton', 'result', 'resultSummary', 'status', 'cardList', 'total', 'cartButton'];
  controller;
  catalog = [];
  matches = [];

  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.refs.pasteTab.addEventListener('click', this.showPaste, { signal });
    this.refs.decksTab.addEventListener('click', this.showDecks, { signal });
    this.refs.themeTab.addEventListener('click', this.showThemes, { signal });
    this.refs.clearButton.addEventListener('click', this.clearList, { signal });
    this.refs.checkButton.addEventListener('click', this.reviewList, { signal });
    this.refs.fileInput.addEventListener('change', this.loadFile, { signal });
    this.refs.cartButton.addEventListener('click', this.goToReview, { signal });
    this.addEventListener('click', this.handleDeckChoice, { signal });
    try {
      const catalog = document.getElementById(this.dataset.catalogId);
      this.catalog = JSON.parse(catalog?.textContent || '[]');
      this.refs.status.textContent = this.dataset.catalogPending;
    } catch {
      this.catalog = [];
      this.refs.status.textContent = this.dataset.addError;
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }

  showPaste = () => this.switchMode('paste');
  showDecks = () => this.switchMode('decks');
  showThemes = () => this.switchMode('themes');

  switchMode(mode) {
    const showPaste = mode === 'paste';
    const showDecks = mode === 'decks';
    const showThemes = mode === 'themes';
    this.refs.pasteTab.classList.toggle('is-active', showPaste);
    this.refs.decksTab.classList.toggle('is-active', showDecks);
    this.refs.themeTab.classList.toggle('is-active', showThemes);
    this.refs.pasteTab.setAttribute('aria-selected', String(showPaste));
    this.refs.decksTab.setAttribute('aria-selected', String(showDecks));
    this.refs.themeTab.setAttribute('aria-selected', String(showThemes));
    this.refs.pastePanel.hidden = !showPaste;
    this.refs.decksPanel.hidden = !showDecks;
    this.refs.themePanel.hidden = !showThemes;
    this.refs.result.hidden = true;
  }

  clearList = () => {
    this.refs.deckList.value = '';
    this.refs.fileInput.value = '';
    this.refs.result.hidden = true;
    this.refs.deckList.focus();
  };

  loadFile = async () => {
    const file = this.refs.fileInput.files?.[0];
    if (!file) return;
    this.refs.deckList.value = await file.text();
    this.refs.result.hidden = true;
  };

  handleDeckChoice = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-deck-example]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const lists = {
      starter: '4 RC - Remote-Controlled Car (12-77)\n4 Tod - All Alone (11-87)\n4 Darkwing Duck - Drake Mallard (11-145)\n4 Zipper - Tiny Helper (12-148)\n4 Zipper - Big Helper (12-150)\n4 Buzz Lightyear - Space Ranger (12-76)\n4 Alpha - Pack Leader (13-151)\n4 Minnie Mouse - Spinning Skater (11-72)\n4 Mulan - Resourceful Recruit (11-69)\n4 Alice - Growing Girl (2-137)\n4 Buzz Lightyear - Grounded (13-76)\n4 The Huntsman - On the Queen\'s Orders (12-94)\n4 Gadget Hackwrench - Resourceful Mechanic (12-145)\n4 Ranger Plane (12-167)\n4 Cri-Kee - Good Luck Charm (10-142)',
      forest: '4 Mickey Mouse - Brave Little Prince (9-111)\n4 Mickey Mouse - True Friend (1-12)\n4 Mickey Mouse - Expedition Leader (12-26)\n2 Mickey Mouse - Amber Champion (10-23)\n4 Pluto - Friendly Pooch (3-18)\n4 Grandmother Willow - Ancient Advisor (11-13)\n4 Hamm - Piggy Bank (12-11)\n4 Rex - Protective Dinosaur (12-10)\n4 Eilonwy - Princess of Llyr (10-7)\n4 Aladdin - Barreling Through (10-123)\n2 Gaston - Superior Archer (13-15)\n4 Recovered Page (10-30)\n3 Metamorphosis (12-31)\n3 When You Need Help, Just Call (12-32)\n3 This Growing Pressure (12-29)\n4 Red Alert (13-135)\n3 The Horseman Strikes! (10-29)',
      tempo: '3 Mushu - Stealthy Dragon\\n1 Mickey Mouse & Minnie Mouse - Adventuring Duo',
    };
    const example = lists[button.dataset.deckExample];
    if (!example) return;
    this.refs.deckList.value = example;
    this.switchMode('paste');
    this.reviewList();
  };

  normalize(value) {
    return value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }

  baseTitle(product) {
    return (product.title || '').split(' — ')[0].trim();
  }

  parseList(text) {
    const counts = new Map();
    const invalid = [];
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line || line.startsWith('#') || line.startsWith('//')) continue;
      let quantity = 1;
      let name = line;
      let match = line.match(/^(\d+)\s*[x×]?\s+(.+)$/i);
      if (match) {
        quantity = Number.parseInt(match[1], 10);
        name = match[2].trim();
      } else {
        match = line.match(/^(.+?)\s+[x×]\s*(\d+)$/i) || line.match(/^(.+?)\s{2,}(\d+)$/);
        if (match) {
          name = match[1].trim();
          quantity = Number.parseInt(match[2], 10);
        }
      }
      if (!name || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) {
        invalid.push(line);
        continue;
      }
      counts.set(name, (counts.get(name) || 0) + quantity);
    }
    return { cards: [...counts].map(([name, requested]) => ({ name, requested })), invalid };
  }

  findProduct(name) {
    const cardRef = name.match(/\((\d{1,2})-(\d{1,3})\)\s*$/);
    const cleanName = name.replace(/\s*\(\d{1,2}-\d{1,3}\)\s*$/, '').trim();
    const key = this.normalize(cleanName);
    let exact = this.catalog.filter((product) => this.normalize(this.baseTitle(product)) === key);
    if (cardRef) {
      const skuPrefix = `S${cardRef[1].padStart(2, '0')}-${cardRef[2].padStart(3, '0')}-`;
      exact = exact.filter((product) => (product.variants || []).some((variant) => (variant.sku || '').startsWith(skuPrefix)));
    } else if (!exact.length) {
      const suffixless = key.replace(/\s+(set|chapter)\s+\d+.*$/, '').trim();
      exact = this.catalog.filter((product) => this.normalize(this.baseTitle(product)) === suffixless);
    }
    return exact;
  }

  money(amount) {
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(Number(amount) || 0);
  }

  reviewList = () => {
    const { cards, invalid } = this.parseList(this.refs.deckList.value);
    this.matches = cards.map((card) => ({ ...card, products: this.findProduct(card.name), variantId: '' }));
    this.refs.cardList.replaceChildren();
    this.refs.result.hidden = false;
    this.refs.cartButton.disabled = true;
    const requestedCopies = cards.reduce((total, card) => total + card.requested, 0);
    this.refs.resultSummary.textContent = this.refs.resultSummary.dataset.template
      .replace('__CARDS__', String(requestedCopies))
      .replace('__NAMES__', String(cards.length));
    this.refs.status.textContent = cards.length ? this.dataset.catalogPending : this.dataset.noCards;

    for (const match of this.matches) this.refs.cardList.append(this.createRow(match));
    for (const line of invalid) {
      const row = document.createElement('li');
      row.className = 'db-card-row';
      const name = document.createElement('span');
      name.className = 'db-card-name';
      name.textContent = line;
      const state = document.createElement('span');
      state.className = 'db-card-status';
      state.textContent = this.dataset.invalidLine;
      row.append(name, state);
      this.refs.cardList.append(row);
    }
    this.refreshSummary();
    this.refs.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  createRow(match) {
    const row = document.createElement('li');
    row.className = 'db-card-row';
    const name = document.createElement('span');
    name.className = 'db-card-name';
    name.textContent = match.name;
    const quantity = document.createElement('span');
    quantity.className = 'db-card-qty';
    quantity.textContent = `× ${match.requested}`;
    const state = document.createElement('span');
    state.className = 'db-card-status';

    if (match.products.length === 0) {
      state.textContent = this.dataset.notListed;
      row.append(name, quantity, state);
      return row;
    }
    if (match.products.length > 1) {
      state.textContent = this.dataset.invalidLine;
      row.append(name, quantity, state);
      return row;
    }

    const product = match.products[0];
    const variants = product.variants || [];
    const availableVariants = variants.filter((variant) => variant.available);
    const normal = availableVariants.find((variant) => /normal/i.test(variant.title));
    const select = document.createElement('select');
    select.className = 'db-variant-select';
    select.setAttribute('aria-label', `${this.dataset.variantLabel || 'Finish'} for ${match.name}`);

    if (!normal && availableVariants.length) {
      const prompt = document.createElement('option');
      prompt.value = '';
      prompt.textContent = 'Choose a finish — Normal unavailable';
      prompt.disabled = true;
      prompt.selected = true;
      select.append(prompt);
      match.requiresFinishChoice = true;
    } else {
      match.requiresFinishChoice = false;
    }

    for (const variant of variants) {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = `${variant.title} · ${this.money(variant.price)}${variant.available ? '' : ` · ${this.dataset.soldOut}`}`;
      option.disabled = !variant.available;
      option.dataset.available = String(Boolean(variant.available));
      option.dataset.price = String(variant.price);
      if (normal && String(variant.id) === String(normal.id)) option.selected = true;
      select.append(option);
    }

    if (normal) {
      match.variantId = String(normal.id);
      select.value = String(normal.id);
      state.textContent = `${normal.title} · ${this.dataset.inStock}`;
    } else if (availableVariants.length) {
      match.variantId = '';
      select.value = '';
      state.textContent = 'Normal unavailable — choose a finish';
      state.classList.add('is-warning');
    } else {
      match.variantId = '';
      select.disabled = true;
      state.textContent = this.dataset.soldOut;
    }

    select.addEventListener('change', () => {
      match.variantId = select.value;
      const selected = variants.find((variant) => String(variant.id) === match.variantId);
      if (match.requiresFinishChoice) {
        if (selected) {
          state.classList.remove('is-warning');
          state.textContent = `${selected.title} selected · ${this.dataset.inStock}`;
        } else {
          state.classList.add('is-warning');
          state.textContent = 'Normal unavailable — choose a finish';
        }
      } else {
        state.textContent = selected ? `${selected.title} · ${this.dataset.inStock}` : this.dataset.soldOut;
      }
      this.refreshSummary();
    }, { signal: this.controller.signal });
    row.append(name, quantity, select, state);
    return row;
  }

  refreshSummary() {
    const items = this.matches.map((match) => {
      const product = match.products.length === 1 ? match.products[0] : null;
      const variant = product?.variants?.find((entry) => String(entry.id) === match.variantId);
      return { match, variant };
    }).filter(({ variant }) => variant?.available);
    const total = items.reduce((sum, { match, variant }) => sum + Number(variant.price || 0) * match.requested, 0);
    const finishChoiceNeeded = this.matches.some((match) => match.requiresFinishChoice && !match.variantId);
    this.refs.total.textContent = this.money(total);
    this.refs.cartButton.disabled = items.length === 0 || finishChoiceNeeded;
    if (!this.matches.length) this.refs.status.textContent = this.dataset.noCards;
    else if (finishChoiceNeeded) this.refs.status.textContent = 'Choose a finish for every card marked “Normal unavailable” before adding to cart.';
    else if (!items.length) this.refs.status.textContent = this.dataset.soldOut;
    else this.refs.status.textContent = `${items.length} ${this.dataset.cartSummary}`;
  }

  goToReview = () => {
    if (this.matches.some((match) => match.requiresFinishChoice && !match.variantId)) {
      this.refs.status.textContent = 'Choose a finish for every card marked “Normal unavailable” before continuing.';
      return;
    }
    const items = this.matches.map((match) => {
      const product = match.products.length === 1 ? match.products[0] : null;
      const variant = product?.variants?.find((entry) => String(entry.id) === match.variantId);
      if (!variant?.available) return null;
      return {
        id: Number(variant.id),
        title: product.title || match.name,
        url: product.url || '',
        image: product.image || '',
        isLocation: (product.tags || []).some((tag) => String(tag).toLowerCase() === 'location'),
        variantTitle: variant.title || '',
        quantity: match.requested,
        unitPrice: Number(variant.price || 0),
      };
    }).filter(Boolean);
    if (!items.length) {
      this.refs.status.textContent = this.dataset.soldOut;
      return;
    }
    try {
      window.sessionStorage.setItem('dicesDeckReview', JSON.stringify(items));
      window.location.assign(this.dataset.reviewUrl);
    } catch {
      this.refs.status.textContent = 'Unable to open the card review. Please check your browser settings and try again.';
    }
  };

}

if (!customElements.get('dices-deck-builder-component')) {
  customElements.define('dices-deck-builder-component', DicesDeckBuilder);
}
